/**
 * One-shot "what's new" dialog.
 *
 * On every login, the app reads `whatsNewSeenVersion` from the local
 * UI prefs. If it doesn't match `CURRENT_VERSION`, a dialog opens with
 * the changelog bullets and a "got it" button that marks this version
 * as seen. After that, the dialog never opens for this version on this
 * browser.
 *
 * Bump CURRENT_VERSION + add an entry to ENTRIES when shipping a wave
 * of user-facing changes you want everyone to see at least once.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useUiPrefsStore } from '../../stores/uiPrefsStore';
import { useAuthStore } from '../../stores/authStore';

/** Bump when shipping a wave of user-facing changes worth announcing. */
const CURRENT_VERSION = '2026-06-14';

interface Entry {
  title: string;
  body: string;
}

const ENTRIES: Entry[] = [
  {
    title: 'תפריט צד מתקפל',
    body: 'הקטגוריות בתפריט נפתחות במצב אקורדיון (אחת בכל פעם) או פלייאאוט צבעוני — לבחירתך בדף "התאמה אישית".',
  },
  {
    title: 'התאמה אישית בלחיצה אחת',
    body: 'דף חדש ב-/personalize: סגנון תפריט, ערכת צבעים, צפיפות, מועדפים והיסטוריה — הכל במקום אחד.',
  },
  {
    title: 'הצצה מהירה ללקוחות',
    body: 'מעבר עם העכבר על שם לקוח בכל מקום במערכת פותח כרטיס תצוגה מקדימה עם הפרטים העיקריים.',
  },
  {
    title: 'התראת ניתוק חיבור',
    body: 'אם הרשת נפלה תוך כדי עבודה תופיע פס התראה צהוב בראש המסך, וירוק כשהיא חוזרת.',
  },
  {
    title: 'הגנה מקרסה',
    body: 'במקום מסך לבן בקריסה של דף — מוצג עכשיו כרטיס שגיאה ידידותי עם אפשרות לחזור לדאשבורד.',
  },
];

export default function WhatsNewPopup() {
  const seen = useUiPrefsStore((s) => s.whatsNewSeenVersion);
  const markSeen = useUiPrefsStore((s) => s.markWhatsNewSeen);
  const token = useAuthStore((s) => s.token);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Wait a tick after login so the popup doesn't fight the login
    // redirect animation. The store is hydrated synchronously from
    // localStorage so `seen` is reliable on the very first mount.
    if (!token) return;
    if (seen === CURRENT_VERSION) return;
    const t = window.setTimeout(() => setOpen(true), 800);
    return () => window.clearTimeout(t);
  }, [token, seen]);

  const handleClose = () => {
    setOpen(false);
    markSeen(CURRENT_VERSION);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 3, py: 2,
        background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
        color: 'white',
      }}>
        <AutoAwesomeIcon />
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>חדש בגרסה</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>{CURRENT_VERSION}</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {ENTRIES.map((e) => (
            <Stack key={e.title} direction="row" spacing={1.5}>
              <Box sx={{
                mt: 0.5,
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: 'primary.main', flexShrink: 0,
              }} />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.25 }}>{e.title}</Typography>
                <Typography variant="body2" color="text.secondary">{e.body}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" onClick={handleClose}>הבנתי</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
